import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAppPublicPathUrl, getRootDomainUrl } from '../utils/tenant';
import axiosInstance from '../api/axios';
import {  User, Mail, Building, MessageSquare, CheckCircle, Send } from 'lucide-react';

function SignUp({ onSignIn }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/api/send-access-request', formData);
      
      if (response.data.success) {
        setLoading(false);
        setSubmitted(true);
        
        // Redirect after 5 seconds
        setTimeout(() => {
          window.location.href = getRootDomainUrl('/');
        }, 5000);
      }
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.error || 'Failed to submit request. Please try again.');
    }

    /* Original signup functionality - commented out for request access form
    try {
      const response = await axiosInstance.post('/api/signup', {
        username,
        password
      });

      // Store user hash in localStorage for session management
      localStorage.setItem('userHash', response.data.user.user_hash);
      localStorage.setItem('username', response.data.user.username);
      
      // Call parent onSignIn callback
      onSignIn(response.data.user);
      
      // Navigate to home page
      window.location.href = getRootDomainUrl('/home');
    } catch (error) {
      setError(error.response?.data?.error || 'Sign up failed');
    } finally {
      setLoading(false);
    }
    */
  };

  const handleSignInClick = () => {
    window.location.href = getAppPublicPathUrl('/signin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-full opacity-10 animate-spin" style={{ animationDuration: '20s' }}></div>
      </div>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl relative z-10 animate-fadeIn">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-30 animate-pulse"></div>
            <h1 className="relative text-4xl font-bold text-blue-800" style={{
              fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
              letterSpacing: "0.2em",
              fontWeight: "700",
              textTransform: "uppercase"
            }}>
              SKEPTICS
            </h1>
          </div>
        </div>
        <p className="mt-2 text-center text-lg text-gray-600 animate-slideUp" style={{ animationDelay: '0.1s' }}>
          Request Platform Access
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl relative z-10">
        <div className="bg-white/80 backdrop-blur-lg py-10 px-6 shadow-2xl rounded-2xl sm:px-12 md:px-16 border border-white/50 animate-slideUp" style={{ animationDelay: '0.15s' }}>
          {submitted ? (
            <div className="text-center py-12 animate-fadeIn">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h3>
              <p className="text-gray-600">We'll review your request and get back to you soon.</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg animate-shake">
                  {error}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {/* Name Field */}
                <div className="animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="animate-fadeIn" style={{ animationDelay: '0.25s' }}>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Company Field */}
                <div className="animate-fadeIn" style={{ animationDelay: '0.3s' }}>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400"
                      placeholder="Acme Inc."
                    />
                  </div>
                </div>

                {/* Role Field */}
                <div className="animate-fadeIn" style={{ animationDelay: '0.35s' }}>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="role"
                      name="role"
                      required
                      value={formData.role}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400 bg-white"
                    >
                      <option value="">Select your role</option>
                      <option value="CFO">CFO</option>
                      <option value="Controller">Controller</option>
                      <option value="Internal Audit">Internal Audit</option>
                      <option value="SOX Manager">SOX Manager</option>
                      <option value="Compliance Officer">Compliance Officer</option>
                      <option value="IT Manager">IT Manager</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Message Field */}
              <div className="animate-fadeIn" style={{ animationDelay: '0.4s' }}>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your compliance needs (optional)
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400 resize-none"
                    placeholder="What compliance challenges are you looking to solve?"
                  />
                </div>
              </div>

              <div className="animate-fadeIn" style={{ animationDelay: '0.45s' }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-700 to-blue-800 hover:from-indigo-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting request...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Send className="h-5 w-5 mr-2" />
                      Request Access
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

            <div className="text-center animate-fadeIn mt-4" style={{ animationDelay: '0.5s' }}>
              <button
                type="button"
                onClick={handleSignInClick}
                className="text-sm text-gray-600 hover:text-blue-800 underline transition-all duration-200 transform hover:scale-105"
              >
                Already have access? Sign in
              </button>
            </div>
        </div>
      </div>
      
      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default SignUp;